/**
 * Responsibility: show how much room this account has left, and where it went.
 *
 * A total on its own tells somebody they are full without telling them what to
 * delete, so the breakdown is the point rather than decoration. Value history
 * is the row that grows on its own - one reading per holding per day - and it
 * is the one people are surprised by.
 *
 * The figures come from the hub, which measures by adding the documents up:
 * Firestore reports storage per project and never per user, so there is no
 * cheaper answer. It is cached for half a minute, which is why the refresh
 * exists - without it a deletion appears to do nothing.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { hubApi, type StorageUsage } from '../../api/client';

/** Bytes as something a person reads, not a number with ten digits. */
function readable(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** The API's collection names are storage keys; these are what people call them. */
const LABELS: Record<string, string> = {
  holdings: 'Collection',
  likes: 'Likes',
  searches: 'Saved searches',
  topics: 'News topics',
  bins: 'Binned cards',
  channels: 'Notification channels',
  watchSubs: 'Watchlist',
  signalState: 'Drop signals seen',
  gradingList: 'Grading shortlist',
  settings: 'Settings',
};

function barColour(level: string): 'primary' | 'warning' | 'error' {
  if (level === 'full') return 'error';
  return level === 'warning' ? 'warning' : 'primary';
}

function Message({ usage }: { usage: StorageUsage }) {
  if (usage.level === 'full') {
    return (
      <Alert severity="error">
        Your storage is full. New holdings, likes and searches will be refused until
        you remove something.
      </Alert>
    );
  }
  if (usage.level === 'warning') {
    return (
      <Alert severity="warning">
        You have used {usage.percent}% of your storage. Nothing is refused yet, but
        this is a good moment to clear out what you no longer want.
      </Alert>
    );
  }
  return null;
}

export function StorageUsageCard() {
  const client = useQueryClient();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['account', 'usage'],
    queryFn: () => hubApi.usage(),
  });

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Storage</Typography>
          <Tooltip title="Recount now">
            <span>
              <IconButton
                size="small"
                disabled={isFetching}
                onClick={() => {
                  void hubApi.usage(true).then((fresh) => {
                    client.setQueryData(['account', 'usage'], fresh);
                  });
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Divider />

        {isLoading && <LinearProgress />}
        {isError && (
          <Typography variant="body2" color="text.secondary">
            Storage could not be measured. The analysis service may be offline.
          </Typography>
        )}

        {data && (
          <>
            <Message usage={data} />

            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  {readable(data.bytes_used)} of {readable(data.limit_bytes)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {readable(data.bytes_remaining)} left
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                // Always visible: at real usage this bar is a fraction of a
                // percent, and a bar with nothing in it looks broken.
                value={Math.max(Math.min(data.percent, 100), 0.5)}
                color={barColour(data.level)}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>

            <Table size="small">
              <TableBody>
                {data.collections.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell sx={{ border: 0, pl: 0 }}>
                      {LABELS[row.name] ?? row.name}
                    </TableCell>
                    <TableCell sx={{ border: 0 }} align="right">
                      <Typography variant="body2" color="text.secondary">
                        {row.documents} {row.documents === 1 ? 'record' : 'records'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0 }} align="right">
                      {readable(row.bytes_used)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="caption" color="text.secondary">
              {data.documents} records in total. Figures include the search indexes
              Firestore keeps alongside each record, so they are what the project is
              actually charged for rather than the raw text.
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}
